import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getIncidentById, updateIncident } from '@/api/incidents';
import { Incident } from '@/types/community/incident';
import { useAuth } from '@/contexts/AuthContext';
import IncidentModal from '@/components/features/community/IncidentModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommentsFormSection from '@/components/common/CommentsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteIncident } from '@/api/incidents';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const incidentStatusColors: Record<Incident['status'], string> = {
  reported: 'orange',
  investigating: 'blue',
  in_progress: 'cyan',
  resolved: 'green',
  closed: 'default'
};

const incidentPriorityColors: Record<Incident['priority'], string> = {
  low: 'green',
  medium: 'gold',
  high: 'orange',
  critical: 'red'
};

const incidentTypeColors: Record<Incident['type'], string> = {
  safety: 'red',
  security: 'purple',
  untidy: 'orange',
  noise: 'blue',
  maintenance: 'geekblue',
  other: 'default'
};

const IncidentDetailsPage: React.FC = () => {
  const { t } = useTranslation(['incidentReporting', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { incident: Incident } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - CRITICAL: Only fetch if no state data is available
  const { data: incident, isLoading } = useQuery({
    queryKey: ['incidents', id],
    queryFn: () => getIncidentById(id!, firestoreUser!),
    enabled: !!id && !state?.incident && !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayIncident = state?.incident || incident;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (incidentId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteIncident(incidentId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('incidentReporting:incidentDeleted'));
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/incidents');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['incidents', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Comment handlers
  const handleAddComment = async (comment: any) => {
    if (!displayIncident || !firestoreUser) return;

    try {
      console.log('Adding comment:', comment);
      console.log('Current displayIncident:', displayIncident);
      
      const newComment = {
        ...comment,
        createdAt: new Date()
      };

      const updatedComments = [...(displayIncident.comments || []), newComment];
      console.log('Updated comments:', updatedComments);

      await updateIncident(displayIncident.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:createSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['incidents', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
    } catch (error: any) {
      console.error('Error adding comment:', error);
      message.error(error.message || t('common:createError'));
    }
  };

  const handleUpdateComment = async (commentIndex: number, content: string) => {
    if (!displayIncident || !firestoreUser) return;

    try {
      const updatedComments = [...(displayIncident.comments || [])];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],
        content
      };

      await updateIncident(displayIncident.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:updateSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['incidents', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
    } catch (error: any) {
      console.error('Error updating comment:', error);
      message.error(error.message || t('common:updateError'));
    }
  };

  const handleDeleteComment = async (commentIndex: number) => {
    if (!displayIncident || !firestoreUser) return;

    try {
      const updatedComments = [...(displayIncident.comments || [])];
      updatedComments.splice(commentIndex, 1);

      await updateIncident(displayIncident.id!, { comments: updatedComments }, firestoreUser);
      
      message.success(t('common:deleteSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['incidents', id] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      message.error(error.message || t('common:deleteError'));
    }
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.incident) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayIncident) {
    return <Card>Incident not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayIncident.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content */}
      <Card>
        <Descriptions bordered column={1}>
          <Descriptions.Item label={t('incidentReporting:table.title')}>
            <Text strong>{displayIncident.title}</Text>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.description')}>
            <Paragraph>{displayIncident.description}</Paragraph>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.type')}>
            <Tag color={incidentTypeColors[displayIncident.type]}>
              {t(`incidentReporting:types.${displayIncident.type}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.priority')}>
            <Tag color={incidentPriorityColors[displayIncident.priority]}>
              {t(`incidentReporting:priorities.${displayIncident.priority}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.status')}>
            <Tag color={incidentStatusColors[displayIncident.status]}>
              {t(`incidentReporting:statuses.${displayIncident.status}`)}
            </Tag>
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.reportedBy')}>
            {displayIncident.reportedBy.name}
          </Descriptions.Item>
          
          <Descriptions.Item label={t('incidentReporting:table.reportedAt')}>
            {dayjs(displayIncident.reportedAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
          
          {displayIncident.block && (
            <Descriptions.Item label={t('incidentReporting:table.block')}>
              {displayIncident.block.name}
            </Descriptions.Item>
          )}
          
          {displayIncident.apartmentUnit && (
            <Descriptions.Item label={t('incidentReporting:table.apartmentUnit')}>
              {displayIncident.apartmentUnit.name}
            </Descriptions.Item>
          )}
          
          {displayIncident.amenity && (
            <Descriptions.Item label={t('incidentReporting:table.amenity')}>
              {displayIncident.amenity.name}
            </Descriptions.Item>
          )}
          
          {displayIncident.assignedTo && (
            <Descriptions.Item label={t('incidentReporting:table.assignedTo')}>
              {displayIncident.assignedTo.name}
            </Descriptions.Item>
          )}
          
          {displayIncident.resolvedAt && (
            <Descriptions.Item label={t('incidentReporting:table.resolvedAt')}>
              {dayjs(displayIncident.resolvedAt).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          )}
          
          {displayIncident.attachments && displayIncident.attachments.length > 0 && (
            <Descriptions.Item label={t('incidentReporting:table.attachments')}>
              {displayIncident.attachments.length} {t('common:items')}
            </Descriptions.Item>
          )}
        </Descriptions>
        
        {/* Comments Section */}
        <div style={{ marginTop: 24 }}>
          <Typography.Title level={4}>{t('common:comments')}</Typography.Title>
          <CommentsFormSection
            comments={displayIncident.comments || []}
            onAddComment={handleAddComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            showForm={true}
            maxHeight="400px"
          />
        </div>
      </Card>

      {/* Edit Modal */}
      <IncidentModal
        open={isEditModalOpen}
        incident={displayIncident}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('incidentReporting:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default IncidentDetailsPage; 